import React, { useState, useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _getSelectedOption,
  _getBaseUrl,
  _isFirstRender,
  _getCSRFTokenValue,
  _getTileShape,
} from "../utils/helper";
import api from "../utils/api";
import { Loader, Pagination } from "../atoms";
import { InspiratiepuntenAdminDashboardListView, Modal, PostSavedMessage } from "../molecules";
import StateView from "../utils/StateView";

const INITIAL_VALUES = {
  keywords: [],
  type: [],
  transitieagenda: [],
  postStatus: ['1', '3', '9'], // 1: Concept, 2: Awaitinging approval, 3: Published, 9: Archived
  nodeStatus: [true, false],
  orderBy: "changed",
  uid: null,
  userRoles: [],
  page: 1,
  postsPerPage: 10,
};

const InspiratiepuntenDashboard = (props) => {
  // For pending approval posts
  const [isLoadingPendingApprovalPosts, setIsLoadingPendingApprovalPosts] = useState(false);
  const [pendingApprovalPosts, setPendingApprovalPosts] = useState([]);
  const [selectedPendingApprovalPost, setSelectedPendingApprovalPost] = useState([]);
  const [processType, setProcessType] = useState(null);

  // Modal
  const [openApprovalModal, setOpenApprovalModal] = useState(false);
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState(null);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [openUnpublishModal, setOpenUnpublishModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // For other posts
  const [values, setValues] = useState(INITIAL_VALUES);
  const [options, setOptions] = useState(null);
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isFirstLoadPosts, setIsFirstLoadPosts] = useState(true);

  // Pagination
  const [totalPages, setTotalPages] = useState(0);

  const [deletePostNid, setDeletePostNid] = useState(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  const [showPostSavedMessage, setShowPostSavedMessage] = useState(null);

  useEffect(() => {
    fetchOptions();

    fetchPosts(
      {
        nodeStatus: [true, false],
        postStatus: ['2'],
        orderBy: "changed",
        postsPerPage: 99999,
        page: 1,
        uid: null,
        userRoles: JSON.parse(props.data.userroles),
      },
      'pending_approval'
    );

    fetchPosts(values);

    const urlParams = new URLSearchParams(window.location.search);    
    if ([...urlParams].length > 0) {
      if (urlParams.has('t')) {
        setTimeout(() => {
          setShowPostSavedMessage({ title: decodeURIComponent(urlParams.get('t')) });
        }, 1500);
      }
    }
  }, []);

  // useEffect(() => {
  //   fetchPosts(values, 'other');
  // }, [values]);

  const fetchOptions = async () => {
    api
      .get("/inspiratiepunten_filteroptions")
      .then((res) => {
        // setIsLoadingPosts(false);
        console.log(res.data.options);
        setOptions(res.data.options);
      })
      .catch(() => {
        // setIsLoadingPosts(false);
      });
  };

  const fetchPosts = async (values, type = 'other') => {
    if (type === 'pending_approval') {
      setIsLoadingPendingApprovalPosts(true);
    } else {
      setIsLoadingPosts(true);
    }

    const csrfToken = await _getCSRFTokenValue();
    const data = values;

    api
      .post("/inspiratiepunten_searchandfilter", data, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        if (type === 'pending_approval') {
          setPendingApprovalPosts(res.data.posts);
        } else {
          setPosts(res.data.posts);
          setTotalPages(res.data.totalPages);
          setIsFirstLoadPosts(false);
        }

        if (type === 'pending_approval') {
          setIsLoadingPendingApprovalPosts(false);
        } else {
          setIsLoadingPosts(false);
        }
      })
      .catch(() => {
        setIsFirstLoadPosts(false);

        if (type === 'pending_approval') {
          setIsLoadingPendingApprovalPosts(false);
        } else {
          setIsLoadingPosts(false);
        }
      });
  };

  const updatePostStatus = async (processPosts, postStatus, setOpenModal) => {
    setIsProcessing(true);
    setModalErrorMessage(null);

    const csrfToken = await _getCSRFTokenValue();
    const data = {
      nid: processPosts,
      postStatus: postStatus,
    };

    api
      .post("/inspiratiepunten_update_post_status", data, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        setIsProcessing(false);

        if (postStatus === '1' && values.postStatus.includes('1')) { // Concept
          fetchPosts(values);
        }

        if (postStatus === '3' && values.postStatus.includes('3')) { // Published
          fetchPosts(values);
        }

        if (postStatus === '9' && values.postStatus.includes('9')) { // Archived
          fetchPosts(values);
        }

        // Remove processPosts from list
        setPendingApprovalPosts(pendingApprovalPosts.filter(post => !processPosts.includes(post.nid)));
        setOpenModal(false);
        setSelectedPosts([]);
      })
      .catch((err) => {
        // console.error(err.data.error);
        setModalErrorMessage(err.data.error);
        setIsProcessing(false);
      });
  }

  const deletePosts = async (nids) => {
    if (!nids || nids.length === 0) return;

    setIsDeletingPost(true);

    const csrfToken = await _getCSRFTokenValue();

    try {
      // Create an array of promises for each deletion
      const deletePromises = nids.map((nid) => {
        const data = { nid };
        return api.post("/inspiratiepunten_node_delete", data, {
          headers: { "X-CSRF-Token": csrfToken },
        });
      });

      // Wait for all deletions to complete
      await Promise.all(deletePromises);

      // Remove deleted posts from state
      setPosts(posts.filter(post => !nids.includes(post.nid)));
      setDeletePostNid(null); // optional
    } catch (err) {
      console.error("Failed to delete some posts:", err);
    } finally {
      setIsDeletingPost(false);
    }
  };


  const getPostTitle = (nid, searchPosts) => {
    const post = searchPosts.find(post => post.nid === nid);

    if (post) {
      return post.title;
    }

    return "";
  }

  const isAllSelectedPostsPublished = () => {
    if (selectedPosts.length === 0) {
      return false;
    }

    for (let i = 0; i < selectedPosts.length; i++) {
      const post = posts.find(post => post.nid === selectedPosts[i]);

      if (post && post.postStatus.value !== "3") {
        return false;
      }
    }

    return true;
  }

  const isPubishedPostsSelected = () => {
    if (selectedPosts.length === 0) {
      return true;
    }

    for (let i = 0; i < selectedPosts.length; i++) {
      const post = posts.find(post => post.nid === selectedPosts[i]);

      if (post && post.postStatus.value === "3") {
        console.log("true for post:", post.nid);
        return true;
      }
    }

    return false;
  }

  const downloadCSV = (values) => {
    let restUrl = _getBaseUrl() + 'praktijkervaring/csv-export/projects';

    let params = {};

    if (values.keywords.length > 0) {
      params['keywords'] = [values.keywords[0]];
    }

    if (values.postStatus.length > 0) {
      params['postStatus'] = values.postStatus;
    }

    if (values.transitieagenda.length > 0) {
      params['transitieagenda'] = [values.transitieagenda[0]];
    }

    if (values.type.length > 0) {
      params['type'] = [values.type[0]];
    }

    if (values.uid !== null) {
      params['uid'] = values.uid;
    }

    console.log('Params for CSV export:', params);

    const paramsJson = JSON.stringify(params);

    restUrl += '?data=' + encodeURIComponent(paramsJson);

    console.log('CSV export URL:', restUrl);

    window.open(restUrl, '_blank');
  }

  return (
    <React.Fragment>
      <div className="flex-col md:flex-row flex gap-4 items-center justify-between mt-8 mb-8">
        <h2 className="text-26 font-sharpgrotesk font-semibold">Ter akkoord aangeboden - {pendingApprovalPosts.length} items</h2>

        <div>
          <a href="/praktijkervaring/create" target="_self" className="a-button flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-cirBlack text-white border-cirBlack border hover:bg-white hover:text-cirBlack">+ Nieuw project</a>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[1280px]">
          <div className="iprt-dashboard-projects-list admin relative text-base">
            <InspiratiepuntenAdminDashboardListView
              posts={pendingApprovalPosts}
              selectedPost={selectedPendingApprovalPost}
              setSelectedPost={setSelectedPendingApprovalPost}
              isFirstLoadPosts={isFirstLoadPosts}
              isLoadingPosts={isLoadingPendingApprovalPosts}
            />
            {/* Pending Approval Posts ******************************** END */}
            {/* Approval Modal **************************************** */}
            {
              openApprovalModal &&
              <Modal
                title="Project goedkeuren?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Goedkeuren"
                confirmButtonCss="border-green-800 bg-green-800 text-white hover:bg-white hover:text-green-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenApprovalModal(false);
                }}
                onConfirm={() => {
                  updatePostStatus(selectedPendingApprovalPost, '3', setOpenApprovalModal);
                }}
                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPendingApprovalPost.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }
                          <div key={index} className="text-center">{getPostTitle(nid, pendingApprovalPosts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Approval Modal **************************************** END */}
            {/* Rejection Modal *************************************** */}
            {
              openRejectionModal &&
              <Modal
                title="Project afwijzen?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Afwijzen"
                confirmButtonCss="border-red-800 bg-red-800 text-white hover:bg-white hover:text-red-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenRejectionModal(false);
                }}
                onConfirm={() => {
                  updatePostStatus(selectedPendingApprovalPost, '1', setOpenRejectionModal);
                }}
                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPendingApprovalPost.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }
                          <div key={index} className="text-center">{getPostTitle(nid, pendingApprovalPosts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Rejection Modal *************************************** END */}
          </div>
        </div>
      </div>

      <div className="my-10">
        <div className="flex items-center justify-center gap-2">
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-transparent text-cirBlack border-cirBlack border hover:bg-cirBlack hover:text-white transition-colors ${selectedPendingApprovalPost.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPendingApprovalPost.length === 0}
            onClick={() => {
              setOpenRejectionModal(true);
            }}
          >Afkeuren</button>
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-cirBlack text-white border-cirBlack border hover:bg-white hover:text-cirBlack transition-colors ${selectedPendingApprovalPost.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPendingApprovalPost.length === 0}
            onClick={() => {
              setOpenApprovalModal(true);
            }}
          >Publiceren</button>
        </div>
      </div>

      {/* All posts section */}
      <div className="my-16">
        <h2 className="text-26 font-sharpgrotesk font-semibold mb-8">Alle items</h2>
        {options !== null && (
          <div id="projectForm">
            <div className="my-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {/* User */}
                <div>
                  <label htmlFor="user" className="block">Gebruiker</label>
                  <div className="relative select-arrow">
                    <select id="user" name="user"
                      value={values.uid == null ? '' : values.uid}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === "") {
                          setValues({
                            ...values,
                            uid: INITIAL_VALUES.uid
                          });
                        } else {
                          setValues({
                            ...values,
                            uid: value
                          });
                        }
                      }}
                      class="w-full border p-2 appearance-none select-arrow bg-white force-white-select">
                      <option value="" selected={values.uid == null}>Alles</option>
                      {
                        options.transitieagenda_users.length > 0 && options.transitieagenda_users.map((user, index) => (
                          <option key={index} value={user.value} selected={values.uid === user.value}>{user.label}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>
                {/* Type */}
                <div>
                  <label htmlFor="type" className="block">Type</label>
                  <div className="relative select-arrow">
                    <select id="type" name="type"
                      onChange={e => {
                        const value = e.target.value;
                        if (value === "") {
                          setValues({
                            ...values,
                            type: INITIAL_VALUES.type
                          });
                        } else {
                          setValues({
                            ...values,
                            type: [value]
                          });
                        }
                      }}
                      class="w-full border p-2 appearance-none select-arrow bg-white force-white-select">
                      <option value="" selected={values.type == []}>Alles</option>
                      {
                        options.project_type.length > 0 && options.project_type.map((type, index) => (
                          <option key={index} value={type.value} selected={values.type[0] === type.value}>{type.label}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>
                {/* Thema */}
                <div>
                  <label htmlFor="transitieagenda" className="block">Thema</label>
                  <div className="relative select-arrow">
                    <select id="transitieagenda" name="transitieagenda"
                      value={values.transitieagenda == [] ? '' : values.transitieagenda[0]}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === "") {
                          setValues({
                            ...values,
                            transitieagenda: INITIAL_VALUES.transitieagenda
                          });
                        } else {
                          setValues({
                            ...values,
                            transitieagenda: [value]
                          });
                        }
                      }}
                      class="w-full border p-2 appearance-none select-arrow bg-white force-white-select">
                      <option value="" selected={values.transitieagenda == []}>Alles</option>
                      {
                        options.transitieagenda.length > 0 && options.transitieagenda.map((transitieagenda, index) => (
                          <option key={index} value={transitieagenda.value} selected={values.transitieagenda[0] === transitieagenda.value}>{transitieagenda.label}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>
                {/* Status */}
                <div>
                  <label htmlFor="status" className="block">Status</label>
                  <div className="relative select-arrow">
                    <select id="status" name="status"
                      value={values.postStatus.length > 1 ? '' : values.postStatus[0]}
                      onChange={e => {
                        const value = e.target.value;
                        if (value === "") {
                          setValues({
                            ...values,
                            postStatus: INITIAL_VALUES.postStatus
                          });
                        } else {
                          setValues({
                            ...values,
                            postStatus: [value]
                          });
                        }
                      }}
                      class="w-full border p-2 appearance-none select-arrow bg-white force-white-select">
                      <option value="" selected={values.postStatus.length > 1}>Alles</option>
                      {
                        options.post_status.length > 0 &&
                        options.post_status
                          .filter((post_status) => post_status.value.toString() !== '2') // Exclude 'Awaiting approval' status
                          .map((post_status, index) => (
                            <option key={index} value={post_status.value} selected={values.postStatus[0] === post_status.value}>{post_status.label}</option>
                          ))
                      }
                    </select>
                  </div>
                </div> 
              </div>
            </div>
            <div>
              <label htmlFor="keyword" className="block">Zoeken</label>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <input
                  type="text" name="keyword" id="keyword" class="w-full border p-2"
                  aria-label="Zoeken"
                  value={values.keywords.length == 0 ? '' : values.keywords[0]}
                  onChange={e => {
                    const value = e.target.value;
                    setValues({
                      ...values,
                      keywords: value ? [value] : []
                    });
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      const value = e.target.value;
                      const newValues = { ...values, keywords: value ? [value] : [] };
                      setValues(newValues);
                      fetchPosts(newValues);
                      setSelectedPosts([]);
                    }
                  }}
                ></input>
              </div>
              <div className="flex items-center justify-start gap-2">
                <button class="flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-fit items-center bg-cirBlack text-white border-cirBlack border hover:bg-white hover:text-cirBlack transition-colors"
                  onClick={() => {
                    // Apply filters
                    const newValues = { ...values, page: 1 };
                    setValues(newValues);
                    fetchPosts(newValues);
                    setSelectedPosts([]);
                  }}
                >Filters toepassen</button>
                <button class="flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-fit items-center bg-transparent text-cirBlack border-cirBlack border hover:bg-cirBlack hover:text-white transition-colors"
                  onClick={() => {
                    const resetValues = { ...INITIAL_VALUES };
                    setValues(resetValues);
                    fetchPosts(resetValues);
                    setSelectedPosts([]);
                  }}
                >Resetten</button>
              </div>
            </div>
          </div>
          // Filters *************************************** END
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[1280px]">
          <div className="iprt-dashboard-projects-list admin relative text-base">
            <InspiratiepuntenAdminDashboardListView
              posts={posts}
              selectedPost={selectedPosts}
              setSelectedPost={setSelectedPosts}
              isFirstLoadPosts={isFirstLoadPosts}
              isLoadingPosts={isLoadingPosts}
            />
            {
              posts.length === 0 || posts === null && (
                <div className="min-h-[150px] h-full flex items-center justify-center">
                  <p className="text-lg">Geen resultaten</p>
                </div>
              )
            }
            {/* Publish Modal **************************************** */}
            {
              openPublishModal &&
              <Modal
                title="Project goedkeuren?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Goedkeuren"
                confirmButtonCss="border-red-800 bg-red-800 text-white hover:bg-white hover:text-red-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenPublishModal(false);
                }}
                onConfirm={() => {
                  updatePostStatus(selectedPosts, '3', setOpenPublishModal);
                }}
                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPosts.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }

                          <div key={index} className="text-center">{getPostTitle(nid, posts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Publish Modal **************************************** END */}

            {/* Unpublish Modal **************************************** */}
            {
              openUnpublishModal &&
              <Modal
                title="Project niet publiceren?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Niet publiceren"
                confirmButtonCss="border-red-800 bg-red-800 text-white hover:bg-white hover:text-red-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenUnpublishModal(false);
                }}
                onConfirm={() => {
                  updatePostStatus(selectedPosts, '1', setOpenUnpublishModal);
                }}
                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPosts.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }

                          <div key={index} className="text-center">{getPostTitle(nid, posts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Unpublish Modal **************************************** END */}

            {/* Archive Modal **************************************** */}
            {
              openArchiveModal &&
              <Modal
                title="Project archiveren?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Archiveren"
                confirmButtonCss="border-red-800 bg-red-800 text-white hover:bg-white hover:text-red-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenArchiveModal(false);
                }}
                onConfirm={() => {
                  updatePostStatus(selectedPosts, '9', setOpenArchiveModal);
                }}
                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPosts.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }

                          <div key={index} className="text-center">{getPostTitle(nid, posts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Archive Modal **************************************** END */}


            {/* Delete Modal **************************************** */}
            {
              openDeleteModal &&
              <Modal
                title="Project verwijderen?"
                // position="absolute"
                blockPage={true}
                confirmButtonLabel="Verwijderen"
                confirmButtonCss="border-red-800 bg-red-800 text-white hover:bg-white hover:text-red-800"
                errorMessage={modalErrorMessage}
                onCancel={() => {
                  setModalErrorMessage(null);
                  setOpenDeleteModal(false);
                }}
                onConfirm={() => {
                  deletePosts(selectedPosts);
                  setOpenDeleteModal(false);
                }}

                isProcessing={isProcessing}
              >
                <div className="grid grid-cols-1 gap-2 my-4">
                  {
                    selectedPosts.map((nid, index) => {
                      return (
                        <React.Fragment>
                          {
                            index > 0 && <div className="border-t border-gray-200" style={{ height: "0px" }}></div>
                          }

                          <div key={index} className="text-center">{getPostTitle(nid, posts)}</div>
                        </React.Fragment>
                      )
                    })
                  }
                </div>
              </Modal>
            }
            {/* Delete Modal **************************************** END */}





          </div>
        </div>
      </div>

      <div className="my-8">
        <div className="flex-wrap flex items-center justify-center gap-2">
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-transparent text-cirBlack border-cirBlack border hover:bg-cirBlack hover:text-white transition-colors ${selectedPosts.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPosts.length === 0}
            onClick={() => {
              setOpenDeleteModal(true);
            }}
          >Verwijderen</button>
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-transparent text-cirBlack border-cirBlack border hover:bg-cirBlack hover:text-white transition-colors ${selectedPosts.length === 0 ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPosts.length === 0}
            onClick={() => {
              setOpenArchiveModal(true);
            }}
          >Archiveren</button>
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-cirBlack text-white border-cirBlack border hover:bg-white hover:text-cirBlack transition-colors whitespace-nowrap ${(selectedPosts.length === 0 || !isAllSelectedPostsPublished()) ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPosts.length === 0 || !isAllSelectedPostsPublished()}
            onClick={() => {
              setOpenUnpublishModal(true);
            }}
          >Publicatie uitzetten</button>
          <button
            className={`flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-cirBlack text-white border-cirBlack border hover:bg-white hover:text-cirBlack transition-colors ${(selectedPosts.length === 0 || isPubishedPostsSelected()) ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={selectedPosts.length === 0 || isPubishedPostsSelected()}
            onClick={() => {
              setOpenPublishModal(true);
            }}
          >Publiceren</button>
        </div>
      </div>

      {(!isFirstLoadPosts && totalPages > 1) &&
        <div className="flex justify-center my-8">
          <Pagination
            totalPages={totalPages}
            page={values.page}
            setPage={(page) => {
              const newValues = { ...values, page: page };
              setValues(newValues);
              fetchPosts(newValues);
            }}
          />
        </div>
      }

      {
        posts.length > 0 &&
        <div className="flex justify-end items-center gap-4">
          <span className="leading-none whitespace-nowrap flex-shrink-0">Overzicht downloaden als</span>
          <button class="flex text-base gap-5 w-min-content py-2.5 px-6 rounded-full relative justify-center border-solid cursor-pointer align-bottom w-full md:w-fit items-center bg-transparent text-cirBlack border-cirBlack border hover:bg-cirBlack hover:text-white transition-colors"
            onClick={() => {
              downloadCSV(values);
            }}
          >CSV</button>
        </div>
      }

      <div className="py-4 lg:py-8"></div>

      {showPostSavedMessage && (
        <PostSavedMessage
          title={showPostSavedMessage.title}
          destroyMessage={() => {
            setShowPostSavedMessage(null);
          }}
        />
      )}

      {/* <StateView title="values" values={values} floating collapsed />
      <StateView
        title="selectedPendingApprovalPost"
        values={selectedPendingApprovalPost}
        floating
        collapsed
        position="top-4 right-48"
      />
      <StateView
        title="selectedPosts"
        values={selectedPosts}
        floating
        collapsed
        position="top-4 right-4"
      />
      <StateView
        title="isAllSelectedPostsPublished"
        values={isAllSelectedPostsPublished()}
        floating
        collapsed
        position="top-1/2 right-4"
      /> */}
    </React.Fragment>
  );
};

export default InspiratiepuntenDashboard;
